import { useRef, useCallback, useContext } from 'preact/hooks';
import { batch } from '@preact/signals';
import {
  messages, isLoading, isStreaming, showTyping, showReview, suggestions,
  addMessage, updateStreamingMessage, finalizeStreamingMessage, removeMessage,
  updateMessageTimestamp, updateMessageMeta, showToast, setOpen,
} from '../store/chatStore.js';
import { ChatContext } from './context.js';
import { msgId } from '../utils/helpers.js';

export function useChat() {
  const { cfg, api, session, bus, analytics, activityRef } = useContext(ChatContext);
  const activityStartedRef = useRef(false);

  const addBot = useCallback((text, animate = true, streaming = false, serverTs = null) => {
    const ts  = serverTs ? new Date(serverTs).getTime() : Date.now();
    const msg = { id: msgId(), role: 'bot', text, timestamp: ts, streaming, animate };
    addMessage(msg);
    if (!streaming) session.appendHistory(msg);
    return msg.id;
  }, [session]);

  const addUser = useCallback((text, serverTs = null) => {
    const ts  = serverTs ? new Date(serverTs).getTime() : Date.now();
    const msg = { id: msgId(), role: 'user', text, timestamp: ts, animate: true };
    addMessage(msg);
    session.appendHistory(msg);
    return msg.id;
  }, [session]);

  const send = useCallback(async (text) => {
    if (!text?.trim() || isLoading.value) return;

    const userMsgId = addUser(text);
    bus.emit('send', { message: text, sessionId: session.getOrCreate().sessionId });

    batch(() => {
      isLoading.value  = true;
      showTyping.value = cfg.showTypingIndicator;
    });

    // Start inactivity observer on first message
    if (!activityStartedRef.current) {
      activityStartedRef.current = true;
      activityRef?.current?.start();
    }

    if (cfg.stream) {
      let botId = null, fullText = '';
      const pendingToolCalls = [];
      let pendingSources = [];

      const ensureBotBubble = () => {
        if (!botId) {
          showTyping.value = false;
          botId = addBot('', true, true);
        }
      };

      await api.send(text, {
        onStart: () => {
          // bubble created lazily on first content (chunk or tool)
        },
        onChunk: (chunk, full) => {
          fullText = full;
          ensureBotBubble();
          // Strip the JSON envelope while streaming — model emits plain text then
          // appends the full JSON blob. Only show the pre-JSON portion in the bubble.
          const jsonStart = full.indexOf('\n\n{');
          const display = jsonStart !== -1 ? full.slice(0, jsonStart) : full;
          updateStreamingMessage(botId, display || full);
        },
        onToolStart: ({ name, callId }) => {
          ensureBotBubble();
          pendingToolCalls.push({ name, callId, result: '', done: false });
          updateMessageMeta(botId, { toolCalls: [...pendingToolCalls] });
        },
        onTool: ({ name, result }) => {
          ensureBotBubble();
          const existing = pendingToolCalls.find(t => t.name === name && !t.done);
          if (existing) { existing.result = result || ''; existing.done = true; }
          else pendingToolCalls.push({ name, result: result || '', done: true });
          updateMessageMeta(botId, { toolCalls: [...pendingToolCalls] });
        },
        onToolError: ({ name, error }) => {
          ensureBotBubble();
          const existing = pendingToolCalls.find(t => t.name === name && !t.done);
          if (existing) { existing.error = error; existing.done = true; }
          else pendingToolCalls.push({ name, error, done: true });
          updateMessageMeta(botId, { toolCalls: [...pendingToolCalls] });
        },
        onSources: (items) => {
          pendingSources = items;
        },
        onDone: (meta) => {
          batch(() => {
            isLoading.value  = false;
            showTyping.value = false;
          });
          if (meta?.user_timestamp) updateMessageTimestamp(userMsgId, meta.user_timestamp);

          if (meta?.error || !botId) {
            const errText = meta?.error || 'The assistant did not return a response. Please try again.';
            if (botId) removeMessage(botId);
            addMessage({
              id: msgId(),
              role: 'system-error',
              text: errText,
              timestamp: Date.now(),
              errorCode: meta?.code || 'empty_response',
              recoverable: meta?.recoverable !== false,
              retryAfter: meta?.retryAfter || null,
              retryText: text,
            });
            bus.emit('receive', { message: errText, code: meta?.code || 'empty_response', sessionId: session.getOrCreate().sessionId, tokens: meta?.tokens });
            return;
          }

          if (botId) {
            const displayText = meta?.structured?.message || fullText;
            finalizeStreamingMessage(botId, displayText, meta?.timestamp);
            const allSources = (meta?.sources?.length ? meta.sources : pendingSources);
            const doneTools  = pendingToolCalls.map(t => ({ ...t, done: true }));
            const metaUpdate = { sources: allSources, toolCalls: doneTools };
            if (meta?.structured) metaUpdate.structured = meta.structured;
            if (allSources.length || doneTools.length || meta?.structured) {
              updateMessageMeta(botId, metaUpdate);
            }
            session.updateLastMessage(botId, displayText);
          }
          if (meta?.structured?.suggestions?.length) {
            suggestions.value = meta.structured.suggestions;
          }
          bus.emit('receive', { message: meta?.structured?.message || fullText, sessionId: session.getOrCreate().sessionId, tokens: meta?.tokens });
        },
        onError: (err) => {
          batch(() => { isLoading.value = false; showTyping.value = false; });
          showToast(
            err.status === 429 ? 'Too many requests. Please wait.' :
            err.status === 503 ? 'Service temporarily unavailable.' :
            cfg.errorMessage
          );
        },
      });
    } else {
      await api.send(text, {
        onDone: (data) => {
          batch(() => { isLoading.value = false; showTyping.value = false; });
          if (data.user_timestamp) updateMessageTimestamp(userMsgId, data.user_timestamp);
          const botId = addBot(data.reply || '', true, false, data.timestamp);
          const metaUpd = {};
          if (data.sources?.length)   metaUpd.sources    = data.sources;
          if (data.structured)        metaUpd.structured = data.structured;
          if (Object.keys(metaUpd).length) updateMessageMeta(botId, metaUpd);
          if (data.structured?.suggestions?.length) suggestions.value = data.structured.suggestions;
          bus.emit('receive', { message: data.reply, sessionId: session.getOrCreate().sessionId, tokens: data.tokens });
        },
        onError: (err) => {
          batch(() => { isLoading.value = false; showTyping.value = false; });
          showToast(err.status === 429 ? 'Too many requests. Please wait.' : cfg.errorMessage);
        },
      });
    }
  }, [cfg, api, session, bus, activityRef, addBot, addUser]);

  return { send, addBot, addUser };
}
